// src/pages/ItemDetails.js
import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaTimes, FaCartPlus, FaArrowUp, FaClock } from 'react-icons/fa';
import Header from '../components/Form/Header';
import MainContent from '../components/Form/MainContent';
import './ItemDetails.css';
import { useCart } from '../contexts/CartContext';
import FooterCart from '../components/Form/FooterCart';
import Modal from '../components/Modal/Modal';
import { useApi } from '../hooks/useApi';
import { formatCurrency } from '../utils/numberUtils';
import { resolveCashBackPercent } from '../utils/cashBackUtils';
import { useTheme } from '../contexts/ThemeContext';
import { applyImageFallback } from '../utils/imageUtils';
import {
  fetchProductAvailability,
  getProductAvailabilityPresentation
} from '../utils/productAvailabilityUtils';
import {
  calcularResumoItemDelivery,
  criarMapaSelecoesConjuntos,
  obterMultiplicadorLimiteConjunto
} from '../utils/ConjuntoGeraQtdUtils';


const ItemDetails = () => {
  const { state } = useLocation();
  const { itemSel, group } = state || {};
  const navigate = useNavigate();
  const { buildApiUrl, fetchData } = useApi();
  const { corApp, corFonteApp } = useTheme();

  const [quantidade, setQuantidade] = useState(1);
  const [observacao, setObservacao] = useState("");
  const [variacao, setVariacao] = useState("Selecione");
  const {cartItemCount, setCartItemCount, cartItemTotal, setCartItemTotal} = useCart();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalErroConjuntoIsOpen, setModalErroConjuntoIsOpen] = useState(false);
  const [adicionaisSelecionados, setAdicionaisSelecionados] = useState([]);
  const [conjuntos, setConjuntos] = useState([]); // dados vindos da API
  const [produtoApresentacoes, setProdutoApresentacoes] = useState([]);
  const [conjuntoItensSelecionados, setConjuntoItensSelecionados] = useState({}); // { conjuntoId: { itemId: quantidade } }

  const [variacaoErro, setVariacaoErro] = useState(false);
  const [adicionalErro, setAdicionalErro] = useState(false);
  const [conjuntoErro, setConjuntoErro] = useState({});
  const [mensagemErroConjunto, setMensagemErroConjunto] = useState("");
  const [apresentacaoId, setApresentacaoId] = useState(null);  
  const [secaoAtiva, setSecaoAtiva] = useState(null);
  const [mostrarNavegacaoConjuntos, setMostrarNavegacaoConjuntos] = useState(false);
  const [configPedido, setConfigPedido] = useState(null);
  const [disponibilidade, setDisponibilidade] = useState(null);
  const [disponibilidadeErro, setDisponibilidadeErro] = useState('');
  const [carregandoDisponibilidade, setCarregandoDisponibilidade] = useState(true);
  const [verificandoInclusao, setVerificandoInclusao] = useState(false);
  const [itemImageOrientation, setItemImageOrientation] = useState('');

  const fetchDataRef = useRef(fetchData);
  const secoesConjuntosRef = useRef({});
  const navegacaoItensRef = useRef({});
  const navegacaoWrapperRef = useRef(null);
  const navegacaoSentinelaRef = useRef(null);
  const observationContainerRef = useRef(null);
  const secoesObserverRef = useRef(null);
  const navegacaoObserverRef = useRef(null);
  const secoesVisiveisRef = useRef({});
  const scrollProgramaticoRef = useRef(null);
  const scrollProgramaticoTimeoutRef = useRef(null);
  const ultimoScrollTopRef = useRef(0);
  fetchDataRef.current = fetchData;
  //const observationTextareaRef = useRef(null);
  //const addToCartButtonRef = useRef(null);

  const getScrollContainer = () => document.querySelector('.main-content');
  // Scrollspy habilitado apenas quando o produto vier configurado para isso.
  const getScrollSpyOffset = () => 140;
  const usarScrollSpy = itemSel?.usarRolagem === true || itemSel?.produto?.usarRolagem === true;
  const debugScrollSpy = true;
  const logScrollSpy = (...args) => {
    if (debugScrollSpy) {
      console.log('[ItemDetails scrollspy]', ...args);
    }
  };
  const getScrollContext = () => {
    const container = getScrollContainer();
    const usaContainer =
      !!container && (container.scrollHeight - container.clientHeight > 24);

    return {
      container,
      root: usaContainer ? container : null,
      usaContainer
    };
  };
  const getSecaoId = (conjuntoId, variacaoNome = '') =>
    variacaoNome ? `${conjuntoId}::${variacaoNome}` : `${conjuntoId}`;
  const secoesNavegacao = useMemo(() => conjuntos.flatMap(conj => {
    const variacoes = [...new Set(
      (conj.conjunto.items || [])
        .map(item => item.variacao?.trim())
        .filter(Boolean)
    )];

    if (variacoes.length > 0) {
      return variacoes.map(variacaoNome => ({
        id: getSecaoId(conj.conjuntoId, variacaoNome),
        label: variacaoNome
      }));
    }

    return [{
      id: getSecaoId(conj.conjuntoId),
      label: conj.conjunto.nome
    }];
  }), [conjuntos]);
  const cashBackPercent = resolveCashBackPercent(configPedido, group, itemSel);
  const disponibilidadeApresentacao = getProductAvailabilityPresentation({
    disponibilidade,
    erro: disponibilidadeErro,
    carregando: carregandoDisponibilidade,
    verificando: verificandoInclusao
  });
  const valorApresentacao = useMemo(() => {
    const apresentacaoSelecionada = produtoApresentacoes.find(
      item => item.apresentacao?.id === apresentacaoId
    );

    return apresentacaoSelecionada?.apresentacao?.valorAdicional ?? 0;
  }, [produtoApresentacoes, apresentacaoId]);
  // Somar os adicionais dos conjuntos e incluir no valor adicional do item.
  const {
    quantidadeAutomatica,
    quantidade: quantidadeEfetiva,
    valorItem,
    valorAdicional,
    valorTotal,
    total
  } = useMemo(() => calcularResumoItemDelivery({
    produto: itemSel,
    conjuntos,
    selecoes: conjuntoItensSelecionados,
    adicionais: adicionaisSelecionados,
    quantidadeManual: quantidade,
    valorApresentacao
  }), [adicionaisSelecionados, conjuntoItensSelecionados, conjuntos, itemSel, quantidade, valorApresentacao]);
  
  useEffect(() => {
    if (itemSel?.variacao?.variacaoItens?.length > 0) {
      const defaultItem = itemSel.variacao.variacaoItens.find(item => item.isDefault);
      if (defaultItem) {
        setVariacao(defaultItem.descricao);
      }
    }
  }, [itemSel]);

  useEffect(() => {
    let ativo = true;

    const carregarDisponibilidade = async () => {
      setCarregandoDisponibilidade(true);
      setDisponibilidadeErro('');

      try {
        const result = await fetchProductAvailability(fetchDataRef.current, itemSel?.id);

        if (ativo) {
          setDisponibilidade(result);
        }
      } catch (error) {
        if (ativo) {
          setDisponibilidade(null);
          setDisponibilidadeErro('Não foi possível verificar a disponibilidade deste produto agora.');
        }
      } finally {
        if (ativo) {
          setCarregandoDisponibilidade(false);
        }
      }
    };

    if (itemSel?.id) {
      carregarDisponibilidade();
    } else {
      setCarregandoDisponibilidade(false);
    }

    return () => {
      ativo = false;
    };
  }, [itemSel?.id]);

  useEffect(() => {
    const fetchDataFromAPI = async () => {
      try {
        const [produtoConjuntoResult, configPedidoResult] = await Promise.all([
          fetchData('/ProdutoConjunto', { produtoId: itemSel?.id, resumido: true }),
          fetchData('/ConfigPedido')
        ]);

        setConfigPedido(configPedidoResult);

        let result = produtoConjuntoResult;

        setConjuntos(result);

        setConjuntoItensSelecionados(criarMapaSelecoesConjuntos(result));

        // 🔥 As imagens dos itens do conjunto são carregadas diretamente pelas URLs binárias.

        // 🔹 ProdutoApresentacao
        result = await fetchData(`/ProdutoApresentacao?produtoId=${itemSel?.id}`);
        setProdutoApresentacoes(result);

        if (result.length > 0) {
          const primeira = result[0].apresentacao;
          setApresentacaoId(primeira.id);

          const conjuntosApresentacao = primeira.apresentacaoConjuntos ?? [];
          setConjuntos(conjuntosApresentacao);
          setConjuntoItensSelecionados(criarMapaSelecoesConjuntos(conjuntosApresentacao));

          // 🔥 As apresentações também usam diretamente as URLs binárias das imagens.
        }

      } catch (error) {
        console.log("Erro ao buscar conjuntos: ", error);
      }
    };

    if (itemSel?.id) fetchDataFromAPI();
  }, []);

  useEffect(() => {
    if (!usarScrollSpy || !secoesNavegacao.length) {
      setSecaoAtiva('concluir');
      setMostrarNavegacaoConjuntos(false);
      return;
    }

    setSecaoAtiva(secoesNavegacao[0].id);
  }, [secoesNavegacao, usarScrollSpy]);

  useEffect(() => {
    if (!usarScrollSpy || !secoesNavegacao.length) {
      setMostrarNavegacaoConjuntos(false);
      return;
    }

    const { container: scrollContainer, root: observerRoot, usaContainer } = getScrollContext();
    const marcadorNavegacao = navegacaoSentinelaRef.current;
    const secoesIds = [...secoesNavegacao.map(secao => secao.id), 'concluir'];

    if (!marcadorNavegacao) {
      return;
    }

    logScrollSpy('configurando observers', {
      secoesIds,
      usaContainer,
      containerAltura: scrollContainer?.clientHeight,
      containerScrollHeight: scrollContainer?.scrollHeight,
      windowInnerHeight: window.innerHeight
    });

    secoesVisiveisRef.current = {};

    navegacaoObserverRef.current?.disconnect();
    navegacaoObserverRef.current = new IntersectionObserver(
      ([entry]) => {
        logScrollSpy('sentinela', {
          isIntersecting: entry.isIntersecting,
          top: entry.boundingClientRect?.top
        });
        setMostrarNavegacaoConjuntos(!entry.isIntersecting);
      },
      {
        root: observerRoot,
        threshold: 0,
        rootMargin: '-56px 0px 0px 0px'
      }
    );

    navegacaoObserverRef.current.observe(marcadorNavegacao);

    const atualizarSecaoAtiva = () => {
      const scrollTopAtual = observerRoot
        ? observerRoot.scrollTop
        : window.scrollY;
      const alturaViewport = observerRoot
        ? observerRoot.clientHeight
        : window.innerHeight;
      const alturaScroll = observerRoot
        ? observerRoot.scrollHeight
        : document.documentElement.scrollHeight;
      const toleranciaFimScroll = 6;
      const estaNoFimDoScroll =
        scrollTopAtual + alturaViewport >= alturaScroll - toleranciaFimScroll;
      const estaDescendo = scrollTopAtual > ultimoScrollTopRef.current;
      const topoReferencia =
        (navegacaoWrapperRef.current?.getBoundingClientRect().bottom ?? getScrollSpyOffset()) + 8;
      const limiteInferiorViewport = alturaViewport - 24;
      const secoesOrdenadas = secoesIds
        .map((secaoId) => {
          const elemento = secaoId === 'concluir'
            ? observationContainerRef.current
            : secoesConjuntosRef.current[secaoId];

          if (!elemento) {
            return null;
          }

          return {
            secaoId,
            top: elemento.getBoundingClientRect().top
          };
        })
        .filter(Boolean);

      if (scrollProgramaticoRef.current) {
        if (scrollProgramaticoRef.current === 'topo') {
          if (scrollTopAtual <= 16) {
            scrollProgramaticoRef.current = null;
          } else {
            setSecaoAtiva('topo');
            return;
          }
        } else {
          const secaoAlvo = secoesOrdenadas.find(
            (secao) => secao.secaoId === scrollProgramaticoRef.current
          );

          if (secaoAlvo && secaoAlvo.top <= topoReferencia) {
            scrollProgramaticoRef.current = null;
          } else {
            ultimoScrollTopRef.current = scrollTopAtual;
            setSecaoAtiva(scrollProgramaticoRef.current);
            return;
          }
        }
      }

      if (scrollTopAtual <= 16) {
        ultimoScrollTopRef.current = scrollTopAtual;
        setSecaoAtiva('topo');
        return;
      }

      if (estaNoFimDoScroll) {
        ultimoScrollTopRef.current = scrollTopAtual;
        setSecaoAtiva('concluir');
        return;
      }

      if (!secoesOrdenadas.length) {
        ultimoScrollTopRef.current = scrollTopAtual;
        return;
      }

      const proximaSecaoVisivelDescendo = estaDescendo
        ? secoesOrdenadas.find(
            (secao) => secao.top > topoReferencia && secao.top <= limiteInferiorViewport
          )?.secaoId
        : null;
      const secoesQueJaApareceram = secoesOrdenadas
        .filter((secao) => secao.top <= topoReferencia)
        .sort((secaoA, secaoB) => secaoB.top - secaoA.top);

      const proximaSecaoAtiva =
        proximaSecaoVisivelDescendo ??
        secoesQueJaApareceram[0]?.secaoId ??
        secoesOrdenadas
          .sort((secaoA, secaoB) => secaoA.top - secaoB.top)[0]
          ?.secaoId;

      if (proximaSecaoAtiva) {
        setSecaoAtiva(proximaSecaoAtiva);
      }

      ultimoScrollTopRef.current = scrollTopAtual;
    };

    secoesObserverRef.current?.disconnect();
    secoesObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionId = entry.target.dataset.sectionId;

          if (sectionId) {
            secoesVisiveisRef.current[sectionId] = entry;
            logScrollSpy('secao observer', {
              sectionId,
              isIntersecting: entry.isIntersecting,
              ratio: entry.intersectionRatio,
              top: entry.boundingClientRect?.top
            });
          }
        });

        atualizarSecaoAtiva();
      },
      {
        root: observerRoot,
        threshold: [0.2, 0.35, 0.5, 0.7],
        rootMargin: `-${getScrollSpyOffset()}px 0px -42% 0px`
      }
    );

    secoesIds.forEach((secaoId) => {
      const elemento = secaoId === 'concluir'
        ? observationContainerRef.current
        : secoesConjuntosRef.current[secaoId];

      if (!elemento) {
        return;
      }

      elemento.dataset.sectionId = secaoId;
      secoesObserverRef.current.observe(elemento);
    });

    const scrollTarget = observerRoot ?? window;
    scrollTarget.addEventListener('scroll', atualizarSecaoAtiva, { passive: true });
    window.addEventListener('resize', atualizarSecaoAtiva);
    atualizarSecaoAtiva();

    return () => {
      if (scrollProgramaticoTimeoutRef.current) {
        window.clearTimeout(scrollProgramaticoTimeoutRef.current);
      }
      scrollTarget.removeEventListener('scroll', atualizarSecaoAtiva);
      window.removeEventListener('resize', atualizarSecaoAtiva);
      secoesObserverRef.current?.disconnect();
      navegacaoObserverRef.current?.disconnect();
    };
  }, [secoesNavegacao, usarScrollSpy]);

  useEffect(() => {
    if (!usarScrollSpy) {
      return;
    }

    const itemAtivo = navegacaoItensRef.current[secaoAtiva];
    const scrollNavegacao = navegacaoWrapperRef.current?.querySelector('.navegacaoConjuntosScroll');

    if (itemAtivo && scrollNavegacao) {
      const proximoScrollLeft =
        itemAtivo.offsetLeft - (scrollNavegacao.clientWidth / 2) + (itemAtivo.clientWidth / 2);

      scrollNavegacao.scrollTo({
        left: Math.max(proximoScrollLeft, 0),
        behavior: 'smooth'
      });
    }
  }, [secaoAtiva, usarScrollSpy]);


  const alterarQtdConjuntoItem = (conjuntoId, itemId, operacao) => {
    setConjuntoItensSelecionados(prev => {
      const atual = { ...(prev[conjuntoId] || {}) };

      // 🔹 Se qtdMax não for informado ou for 0, não aplicar limite
      const conjuntoAtual = conjuntos.find(c => c.conjuntoId === conjuntoId)?.conjunto;
      const qtdMaxRaw = conjuntoAtual?.qtdMax ?? 0;
      const multiplicadorLimite = obterMultiplicadorLimiteConjunto({
        conjunto: conjuntoAtual,
        quantidade: quantidadeEfetiva,
        quantidadeAutomatica
      });
      const qtdMax = qtdMaxRaw > 0 ? qtdMaxRaw * multiplicadorLimite : Infinity;

      const conjuntoItemSel = conjuntos
        .find(c => c.conjuntoId === conjuntoId)
        ?.conjunto.items.find(i => i.id === itemId) ?? null;

      const produtoId = conjuntoItemSel?.produtoId ?? null;
      const descricao = conjuntoItemSel?.produto?.descricao ?? null;
      const valorAdicional = conjuntoItemSel?.valorAdicional ?? 0;

      const totalSelecionado = Object.values(atual).reduce((acc, item) => acc + (item.qtdSel || 0), 0);

      // Clona o item antes de alterar para evitar mutação do estado anterior.
      const itemAtual = atual[itemId]
        ? { ...atual[itemId] }
        : { id: itemId, produtoId, descricao, conjuntoId, qtdSel: 0, valorAdicional };

      if (operacao === 'add' && totalSelecionado < qtdMax) {
        itemAtual.qtdSel += 1;
      } else if (operacao === 'sub' && itemAtual.qtdSel > 0) {
        itemAtual.qtdSel -= 1;
      }

      if (itemAtual.qtdSel === 0) {
        delete atual[itemId];
      } else {
        itemAtual.produtoId = produtoId;
        atual[itemId] = itemAtual;
      }

      // 🔹 Atualiza o estado de erro do conjunto
      setConjuntoErro(prevErros => {
        if (Object.keys(atual).length > 0) {
          const newErros = { ...prevErros };
          delete newErros[conjuntoId];
          return newErros;
        }
        return prevErros;
      });

      return {
        ...prev,
        [conjuntoId]: atual
      };
    });
  };

  if (!itemSel) {
    return <p>Produto não encontrado.</p>;
  }

  const addToCart = async () => {
    if (carregandoDisponibilidade || verificandoInclusao) {
      return;
    }

    setVerificandoInclusao(true);
    setDisponibilidadeErro('');

    try {
      const disponibilidadeAtual = await fetchProductAvailability(fetchDataRef.current, itemSel.id);

      setDisponibilidade(disponibilidadeAtual);
      if (!disponibilidadeAtual?.disponivelAgora) {
        return;
      }
    } catch (error) {
      setDisponibilidade(null);
      setDisponibilidadeErro('Não foi possível verificar a disponibilidade deste produto agora.');
      return;
    } finally {
      setVerificandoInclusao(false);
    }

    const erros = {};
    let mensagens = [];

    if (quantidadeAutomatica && quantidadeEfetiva <= 0) {
      setMensagemErroConjunto("Selecione ao menos um item de um conjunto que gera quantidade.");
      setModalErroConjuntoIsOpen(true);
      return;
    }

    // 🔸 1️⃣ Verificar conjuntos obrigatórios (obrigaSel)
    for (const conj of conjuntos) {
      if (conj.conjunto?.obrigaSel) {
        const selecionados = conjuntoItensSelecionados[conj.conjuntoId] || {};
        const totalSelecionado = Object.values(selecionados)
          .reduce((a, b) => a + (b?.qtdSel || 0), 0);

        const qtdMin = conj.conjunto.qtdMin ?? 0;
        const multiplicadorLimite = obterMultiplicadorLimiteConjunto({
          conjunto: conj.conjunto,
          quantidade: quantidadeEfetiva,
          quantidadeAutomatica
        });
        const minimoNecessario = qtdMin * multiplicadorLimite;

        if (totalSelecionado < minimoNecessario) {
          erros[conj.conjuntoId] = true;

          const faltam = minimoNecessario - totalSelecionado;

          mensagens.push(
            `Você precisa selecionar pelo menos ${minimoNecessario} ${minimoNecessario>1 ? 'itens' : 'item'} para continuar.`
          );
        }
      }
    }

    if (mensagens.length > 0) {
      setConjuntoErro(erros);
      setMensagemErroConjunto(mensagens.join('\n'));
      setModalErroConjuntoIsOpen(true);
      return;
    } else {
      setConjuntoErro({});
    }



    if (Object.keys(erros).length > 0) {
      setConjuntoErro(erros);
      setModalErroConjuntoIsOpen(true);
      return;
    } else {
      setConjuntoErro({});
    }

    // 🔸 2️⃣ Verificar variação obrigatória
    if (
      variacao.toLowerCase().includes('selecione') &&
      itemSel?.variacao?.variacaoItens?.length > 0
    ) {
      setVariacaoErro(true);
      setModalIsOpen(true);
      return;
    }

    const adicionaisObrigatoriosNaoSelecionados = (itemSel.adicionais || [])
      .filter(adicional => adicional.obrigatorio === true)
      .filter(adicional => !adicionaisSelecionados.some(selecionado => selecionado.id === adicional.id));

    if (adicionaisObrigatoriosNaoSelecionados.length > 0) {
      setAdicionalErro(true);
      //setMensagemErroConjunto("Selecione os adicionais obrigatorios para continuar.");
      //setModalErroConjuntoIsOpen(true);
      return;
    }

    setAdicionalErro(false);

    //console.log(conjuntoItensSelecionados); 
    let pedidoItemConjuntos = [];
    Object.entries(conjuntoItensSelecionados).forEach(([conjuntoId, itens]) => {
      const conjuntoSelecionado = conjuntos.find(c => c.conjuntoId === parseInt(conjuntoId));

      Object.entries(itens).forEach(([itemId, itemData]) => {
        if (itemData.qtdSel > 0) {
          pedidoItemConjuntos.push({
            conjuntoId: parseInt(conjuntoId),
            nomeConjunto: conjuntoSelecionado?.conjunto?.nome,
            usarFracao: conjuntoSelecionado?.conjunto?.usarFracao,
            conjuntoItemId: parseInt(itemId),
            descricao: itemData.descricao, // coloque a descrição do item aqui, se quiser
            qtdItens: itemData.qtdSel, // coloque a descrição do item aqui, se quiser
            valorAdicional: itemData.valorAdicional // ou calcule se houver adicional
          });
        }
      });
    });
    

    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    cart.push({
      ...itemSel,
      qtde: quantidadeEfetiva,
      valorItem,
      valorAdicional,
      valorTotal,
      observacao,
      variacao: variacao === "Selecione" ? null : variacao,      
      variacaoDesc: itemSel.variacao?.descricao,
      apresentacaoId,
      adicionaisSelecionados: [...adicionaisSelecionados],
      pedidoItemConjuntos // novo campo
    });
    localStorage.setItem('cart', JSON.stringify(cart));
    localStorage.setItem('dataHoraCart', new Date().toISOString());

    setCartItemCount(cartItemCount+quantidadeEfetiva);
    setCartItemTotal(cartItemTotal+valorTotal);

    setVariacaoErro(false);

    navigate('/ordermenu');
  };

  const toggleAdicional = (adicional) => {
    const existe = adicionaisSelecionados.find(a => a.id === adicional.id);

    let novos;
    if (existe) {
      novos = adicionaisSelecionados.filter(a => a.id !== adicional.id);
    } else {
      novos = [...adicionaisSelecionados, adicional];
    }

    setAdicionaisSelecionados(novos);

    const possuiObrigatorioPendente = (itemSel.adicionais || [])
      .filter(item => item.obrigatorio === true)
      .some(item => !novos.some(selecionado => selecionado.id === item.id));
    setAdicionalErro(possuiObrigatorioPendente);
  };


  const increaseQuantity = () => {
    if (quantidadeAutomatica) return;
    const newQuantity = quantidade + 1;
    setQuantidade(newQuantity);
  };

  const decreaseQuantity = () => {
    if (quantidadeAutomatica) return;
    if (quantidade > 1) {
      const newQuantity = quantidade - 1;
      setQuantidade(newQuantity);
    }
  };

  const cancelAction = () => {
    navigate(-1);
  };  

  const getVariacaoTituloClassName = (variacaoNome) => {
    const nomeNormalizado = variacaoNome?.toLowerCase() || '';

    if (nomeNormalizado.includes('salgado')) {
      return 'is-savory';
    }

    if (nomeNormalizado.includes('doce')) {
      return 'is-sweet';
    }

    return '';
  };

  const getNavegacaoAbaClassName = (isAtivo = false) =>
    `navegacaoAba ${isAtivo ? 'ativo' : ''}`;

  const scrollParaSecao = (secaoId) => {
    setSecaoAtiva(secaoId);
    scrollProgramaticoRef.current = secaoId;
    if (scrollProgramaticoTimeoutRef.current) {
      window.clearTimeout(scrollProgramaticoTimeoutRef.current);
    }
    scrollProgramaticoTimeoutRef.current = window.setTimeout(() => {
      scrollProgramaticoRef.current = null;
    }, 1200);
    const { container: scrollContainer, usaContainer } = getScrollContext();

    if (secaoId === 'topo') {
      if (usaContainer && scrollContainer) {
        scrollContainer.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }

      return;
    }

    const elemento = secaoId === 'concluir'
      ? observationContainerRef.current
      : secoesConjuntosRef.current[secaoId];

    if (!elemento) return;

    if (usaContainer && scrollContainer) {
      const offsetTop =
        elemento.getBoundingClientRect().top -
        scrollContainer.getBoundingClientRect().top +
        scrollContainer.scrollTop -
        getScrollSpyOffset();

      logScrollSpy('scroll click container', { secaoId, offsetTop });
      scrollContainer.scrollTo({
        top: Math.max(offsetTop, 0),
        behavior: 'smooth'
      });
      return;
    }

    const offsetTopWindow =
      elemento.getBoundingClientRect().top +
      window.scrollY -
      getScrollSpyOffset();

    logScrollSpy('scroll click window', { secaoId, offsetTopWindow });
    window.scrollTo({
      top: Math.max(offsetTopWindow, 0),
      behavior: 'smooth'
    });
  };

  const itemImageSource = itemSel.imagemId
    ? buildApiUrl('/ProdutoDelivery/ProdutoImagemArquivo', { produtoId: itemSel.id })
    : '/images/image_default.jpg';

  const handleItemImageLoad = ({ currentTarget }) => {
    const isPortrait = currentTarget.naturalHeight > currentTarget.naturalWidth;
    setItemImageOrientation(isPortrait ? 'is-portrait' : 'is-landscape');
  };

  return (
    <>
      <Header titulo="Adicionar item" />
      {usarScrollSpy && secoesNavegacao.length > 0 && (
        <div
          className={`navegacaoConjuntosWrapper ${mostrarNavegacaoConjuntos ? '' : 'oculta'}`}
          ref={navegacaoWrapperRef}
          style={{
            '--scrollspy-color': corApp,
            '--scrollspy-text-color': corFonteApp
          }}
        >
          <div className="navegacaoConjuntosScroll">
            <button
              type="button"
              ref={(elemento) => {
                if (elemento) {
                  navegacaoItensRef.current.topo = elemento;
                }
              }}
              className={getNavegacaoAbaClassName(secaoAtiva === 'topo')}
              onClick={() => scrollParaSecao('topo')}
            >
              <FaArrowUp /> Topo
            </button>
            {secoesNavegacao.map(secao => (
              <button
                key={`nav-${secao.id}`}
                type="button"
                ref={(elemento) => {
                  if (elemento) {
                    navegacaoItensRef.current[secao.id] = elemento;
                  }
                }}
                className={getNavegacaoAbaClassName(secaoAtiva === secao.id)}
                onClick={() => scrollParaSecao(secao.id)}
              >
                {secao.label}
              </button>
            ))}
            <button
              type="button"
              ref={(elemento) => {
                if (elemento) {
                  navegacaoItensRef.current.concluir = elemento;
                }
              }}
              className={getNavegacaoAbaClassName(secaoAtiva === 'concluir')}
              onClick={() => scrollParaSecao('concluir')}
            >
              Concluir
            </button>
          </div>
        </div>
      )}
      <MainContent className="item-details-main">
        <article className="item-details-page">
        <section className="item-details-product-card" aria-labelledby="item-details-title">
        <h1 className="item-details-title" id="item-details-title">{itemSel.descricao}</h1>
        {disponibilidadeApresentacao.mensagem && (
          <div
            className={`item-details-availability ${disponibilidadeErro ? 'is-error' : ''}`}
            role="alert"
          >
            <FaClock aria-hidden="true" />
            <span>{disponibilidadeApresentacao.mensagem}</span>
          </div>
        )}
        <div className="item-details-summary">
          <figure className={`item-details-image-frame ${itemImageOrientation}`}>
            <img
              src={itemImageSource}
              alt={itemSel.descricao}
              className="item-details-image"
              onLoad={handleItemImageLoad}
              onError={applyImageFallback}
            />
          </figure>

          <div className="item-details-purchase">
            <span className="item-details-quantity-label">Quantidade</span>
            <div className="item-details-stepper" aria-label="Quantidade do produto">
              <button
                type="button"
                className="item-details-stepper-button"
                onClick={decreaseQuantity}
                disabled={quantidadeAutomatica}
                aria-label="Diminuir quantidade"
              >
                −
              </button>
              <strong aria-live="polite">{quantidadeEfetiva}</strong>
              <button
                type="button"
                className="item-details-stepper-button"
                onClick={increaseQuantity}
                disabled={quantidadeAutomatica}
                aria-label="Aumentar quantidade"
              >
                +
              </button>
            </div>
            <p className="item-details-total"><span>Total</span><strong>{formatCurrency(total)}</strong></p>
            {cashBackPercent > 0 && (
              <p className="itemCashbackValue">
                {cashBackPercent}% cashback
              </p>
            )}
          </div>
        </div>

        <div className="item-details-ingredients">
          {itemSel.ingredientes && (
            <div className="item-details-ingredients-text">
              <p><strong>Ingredientes:</strong> {itemSel.ingredientes}</p>
            </div>
          )}
        </div>
        </section>

        {produtoApresentacoes.length > 0 && (
          <section className="item-details-section item-details-presentations">
            <div className="item-details-section-heading">
              <span>Escolha uma opção</span>
              <h2>Apresentação</h2>
            </div>
            {produtoApresentacoes.map(item => {

              return (
                <div key={item.apresentacao.id} className="item-details-presentation-option">
                  <label>
                    <input
                      type="radio"
                      name="apresentacao"
                      //value={item.apresentacao.id}
                      checked={apresentacaoId === item.apresentacao.id}
                      onChange={() => {
                        const conjuntosApresentacao = item.apresentacao.apresentacaoConjuntos ?? [];
                        setConjuntos(conjuntosApresentacao);
                        setConjuntoItensSelecionados(criarMapaSelecoesConjuntos(conjuntosApresentacao));
                        setApresentacaoId(item.apresentacao.id);
                      }}
                    />
                    <span>
                      <strong>{item.apresentacao.nome}</strong> - {item.apresentacao.descricao}
                      {item.apresentacao.valorAdicional > 0 && (
                        <span> (+ {formatCurrency(item.apresentacao.valorAdicional)})</span>
                      )}
                    </span>
                  </label>
                </div>
              );
            })}
          </section>
        )} 

        {usarScrollSpy && secoesNavegacao.length > 0 && (
          <div className="navegacaoSentinela" ref={navegacaoSentinelaRef} aria-hidden="true" />
        )}

        {conjuntos.length > 0 && (
        <div className="item-details-groups">
          {conjuntos.map(conj => {
            const selecionados = conjuntoItensSelecionados[conj.conjuntoId] || {};
            const totalSelecionado = Object.values(selecionados).reduce((a, b) => a + (b?.qtdSel || 0), 0);
            const multiplicadorLimite = obterMultiplicadorLimiteConjunto({
              conjunto: conj.conjunto,
              quantidade: quantidadeEfetiva,
              quantidadeAutomatica
            });

            // 🔹 Agrupa itens por variação
            const itensAgrupados = (conj.conjunto.items || []).reduce((acc, item) => {
              const variacaoNome = item.variacao || "";
              if (!acc[variacaoNome]) acc[variacaoNome] = [];
              acc[variacaoNome].push(item);
              return acc;
            }, {});

            return (
              <div
                key={conj.id}
                ref={(elemento) => {
                  const possuiVariacoes = Object.keys(itensAgrupados).some(nome => nome);

                  if (elemento && !possuiVariacoes) {
                    secoesConjuntosRef.current[getSecaoId(conj.conjuntoId)] = elemento;
                  }
                }}
                className={`item-details-group scrollTargetSection ${conjuntoErro[conj.conjuntoId] ? 'is-error' : ''}`}
              >
                <strong>
                  {conj.conjunto.nome}
                  {conj.conjunto.qtdMax * multiplicadorLimite > 0 && (
                    <> (até {conj.conjunto.qtdMax * multiplicadorLimite} {conj.conjunto.qtdMax * multiplicadorLimite > 1 ? "itens" : "item"})</>
                  )}
                </strong>

                {/* 🔹 Renderiza grupos por variação */}
                {Object.entries(itensAgrupados).map(([variacaoNome, itens]) => (
                  <div
                    key={variacaoNome}
                    ref={(elemento) => {
                      if (elemento && variacaoNome) {
                        secoesConjuntosRef.current[getSecaoId(conj.conjuntoId, variacaoNome)] = elemento;
                      }
                    }}
                    className="item-details-variation-group"
                  >
                    {variacaoNome && (
                      <h4 className={`item-details-variation-title ${getVariacaoTituloClassName(variacaoNome)}`}>{variacaoNome}</h4>
                    )}
                    <ul className="item-details-variation-list">
                      {itens.map(item => (
                        <li
                          key={item.id}
                          className={`item-details-variation-item ${(selecionados[item.id]?.qtdSel || 0) > 0 ? 'is-selected' : ''}`}
                        >
                          <span className="item-details-option-content">
                            {item.produto?.imagemId && (
                              <img
                                src={buildApiUrl('/ProdutoDelivery/ProdutoImagemArquivo', {
                                  produtoId: item.produtoId,
                                })}
                                alt={item.produto?.descricao}
                                className="item-details-option-image"
                                onError={applyImageFallback}
                              />
                            )}

                            <span className="item-details-option-info">
                              <strong>{item.produto.descricao}</strong>
                              {item.valorAdicional > 0 && (
                                <span className="item-details-variation-value">
                                  {conj.conjunto.defineValorItem
                                    ? ` (${formatCurrency(item.valorAdicional)}) `
                                    : ` (+ ${formatCurrency(item.valorAdicional)}) `}
                                </span>
                              )}
                              {conj.conjunto.mostraIngredientes && item.produto?.ingredientes && (
                                <>
                                  <span>{item.produto.ingredientes}</span>
                                </>
                              )}
                            </span>
                          </span>

                          <div className="item-details-option-stepper">
                            <button
                              type="button"
                              className="item-details-option-button"
                              onClick={() => alterarQtdConjuntoItem(conj.conjuntoId, item.id, 'sub')}
                              disabled={(selecionados[item.id]?.qtdSel || 0) === 0}
                              aria-label={`Diminuir ${item.produto.descricao}`}
                            >−</button>
                            <span>{selecionados[item.id]?.qtdSel || 0}</span>
                            <button
                              type="button"
                              className="item-details-option-button"
                              onClick={() => alterarQtdConjuntoItem(conj.conjuntoId, item.id, 'add')}
                              disabled={conj.conjunto.qtdMax > 0 && totalSelecionado >= conj.conjunto.qtdMax * multiplicadorLimite}
                              aria-label={`Adicionar ${item.produto.descricao}`}
                            >+</button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
       

        {itemSel.variacao && itemSel.variacao.variacaoItens?.length > 0 && (
          <section className="item-details-section item-details-select-section">
            <label htmlFor="variacaoSelect"><strong>{itemSel.variacao.descricao}:</strong></label>
            <select
              id="variacaoSelect"
              value={variacao || ""}
              onChange={(e) => {
                setVariacao(e.target.value);
                setVariacaoErro(false); // limpa erro ao mudar
              }}
              className={`item-details-select ${variacaoErro ? 'is-error' : ''}`}
            >
              <option value="Selecione" disabled>Selecione uma opção</option>
              {itemSel.variacao.variacaoItens.map((item) => (
                <option key={item.id} value={item.descricao}>
                  {item.descricao}
                </option>
              ))}
            </select>
            {variacaoErro && (
              <p className="item-details-error-message">
                Por favor, selecione uma opção.
              </p>
            )}
          </section>
        )}


        {itemSel.adicionais?.length > 0 && (
          <section className={`item-details-section item-details-addons ${adicionalErro ? 'is-error' : ''}`}>
            <div className="item-details-section-heading">
              <span>Personalize seu pedido</span>
              <h2>Adicionais</h2>
            </div>
            {itemSel.adicionais.map((adicional) => {
              const adicionalObrigatorioPendente =
                adicionalErro &&
                adicional.obrigatorio === true &&
                !adicionaisSelecionados.some(selecionado => selecionado.id === adicional.id);

              return (
              <label
                key={adicional.id}
                className={`item-details-checkbox ${adicionalObrigatorioPendente ? 'is-required-error' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={adicionaisSelecionados.some(a => a.id === adicional.id)}
                  onChange={() => toggleAdicional(adicional)}
                />
                {adicional.descricao} (+ {formatCurrency(adicional.preco)})
                {/* {adicional.obrigatorio === true && <span className="adicionalObrigatorioTexto">Obrigatorio</span>} */}
              </label>
              );
            })}
          </section>
        )}

        <section className="item-details-section item-details-conclusion observationTarget" ref={observationContainerRef} tabIndex={-1}>
          
          <label className="item-details-observation-label" htmlFor="observation">Observação</label>
          <textarea
            id="observation"
            //ref={observationTextareaRef}
            rows="2"
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            placeholder="Adicione uma observação sobre este item"
            className="item-details-textarea"
          />
          <div className="item-details-actions">
            <button type="button" className="item-details-cancel-button" onClick={cancelAction}>
              <FaTimes /> Cancelar
            </button>
            <button
              type="button"
              className="item-details-add-button"
              onClick={addToCart}
              disabled={disponibilidadeApresentacao.bloqueado || (quantidadeAutomatica && quantidadeEfetiva <= 0)}
            //ref={addToCartButtonRef}
            >
              <FaCartPlus />
              {carregandoDisponibilidade || verificandoInclusao
                ? 'Verificando disponibilidade...'
                : disponibilidade?.disponivelAgora === false
                  ? 'Produto indisponível'
                  : 'Adicionar ao Carrinho'}
            </button>
          </div>
        </section>
        </article>
      </MainContent>
      <FooterCart />

      <Modal
        show={modalIsOpen}
        onClose={() => setModalIsOpen(false)}        
        message={`Informe ${itemSel.variacao?.descricao}!`}
      >
      </Modal>

      <Modal
        show={modalErroConjuntoIsOpen}
        onClose={() => setModalErroConjuntoIsOpen(false)}
        message={mensagemErroConjunto}
      />

    </>
  );
};

export default ItemDetails;
